// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Model Management page shell
// 2. Separation of Concerns - Data load + RBAC-gated actions
// 3. Modular Architecture - Uses services and small presentational pieces
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Model Management — list Ollama tags (pull/delete). Voice/chat model tags are .env only (view-only).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Stack,
} from '@mui/material';
import { Refresh as RefreshIcon, SmartToy as SmartToyIcon } from '@mui/icons-material';

import ActiveModelBadge from './ActiveModelBadge';
import AlertDialog from '../common/AlertDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import PageSectionHeader from '../common/PageSectionHeader';
import modelManagementService from '../../services/modelManagementService';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const PRIMARY = '#13213c';

const ModelManagement = () => {
  const { hasPermission } = useAuth();
  const canReadModels = hasPermission('MODEL_READ');
  const canPullModels = hasPermission('MODEL_SET_ACTIVE');
  const canDelete = hasPermission('MODEL_DELETE');

  const [models, setModels] = useState([]);
  const [activeModel, setActiveModel] = useState('');
  const [voiceStage1Model, setVoiceStage1Model] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'info',
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pullTag, setPullTag] = useState('');
  const [pullBusy, setPullBusy] = useState(false);
  const [voiceProcessLlm, setVoiceProcessLlm] = useState('ollama');
  const [voiceAnalysisQaLlm, setVoiceAnalysisQaLlm] = useState('ollama');
  const [openaiCosts, setOpenaiCosts] = useState(null);
  const [openaiCostsLoading, setOpenaiCostsLoading] = useState(false);
  const [envReadonly, setEnvReadonly] = useState(true);

  const loadOpenaiCosts = useCallback(async () => {
    if (!canReadModels) return;
    try {
      setOpenaiCostsLoading(true);
      const data = await modelManagementService.getOpenAiOrganizationCostsMonth();
      setOpenaiCosts(data);
    } catch (err) {
      setOpenaiCosts({
        success: false,
        configured: false,
        error: getApiErrorMessage(err, 'Failed to load OpenAI organization costs'),
      });
    } finally {
      setOpenaiCostsLoading(false);
    }
  }, [canReadModels]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const modelsData = await modelManagementService.getActiveModels();
      setModels(modelsData.models || []);
      setActiveModel(modelsData.current_model || '');
      setVoiceStage1Model(modelsData.voice_stage1_model || '');
      setVoiceProcessLlm(modelsData.voice_process_llm_provider || 'ollama');
      setVoiceAnalysisQaLlm(modelsData.voice_analysis_qa_llm_provider || 'ollama');
      setEnvReadonly(modelsData.env_readonly !== false);
      await loadOpenaiCosts();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load models'));
    } finally {
      setLoading(false);
    }
  }, [loadOpenaiCosts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePullOnly = async () => {
    const tag = pullTag.trim();
    if (!tag) {
      setAlertDialog({
        open: true,
        title: 'Tag required',
        message: 'Enter an Ollama model tag (e.g. qwen3.5:9b).',
        severity: 'warning',
      });
      return;
    }
    try {
      setPullBusy(true);
      await modelManagementService.pullModel(tag);
      setPullTag('');
      await loadData();
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Pull finished for "${tag}". Set OLLAMA_ACTIVE_MODEL or OLLAMA_VOICE_STAGE1_MODEL in .env to use it, then restart workers.`,
        severity: 'success',
      });
    } catch (err) {
      setAlertDialog({
        open: true,
        title: 'Pull failed',
        message: getApiErrorMessage(err, 'Ollama pull failed'),
        severity: 'error',
      });
    } finally {
      setPullBusy(false);
    }
  };

  const confirmDeleteModel = async () => {
    if (!deleteTarget) return;
    try {
      await modelManagementService.deleteModel(deleteTarget);
      setDeleteTarget(null);
      await loadData();
      setAlertDialog({
        open: true,
        title: 'Success',
        message: 'Model removed from Ollama.',
        severity: 'success',
      });
    } catch (err) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: getApiErrorMessage(err, 'Failed to delete model'),
        severity: 'error',
      });
    }
  };

  if (loading && models.length === 0 && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Model Management...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        minHeight: 'auto',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
      }}
    >
      <PageSectionHeader
        icon={<SmartToyIcon />}
        title="Model Management"
        description="View .env model configuration; pull or remove installed Ollama tags"
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <ActiveModelBadge modelName={activeModel} />
            <Chip
              label={`Voice LLM: ${voiceProcessLlm}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 600 }}
            />
            <Chip
              label={`QA LLM: ${voiceAnalysisQaLlm}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 600 }}
            />
            <Button
              variant="outlined"
              onClick={loadData}
              aria-label="Refresh models"
              sx={{ borderColor: PRIMARY, color: PRIMARY }}
            >
              <RefreshIcon />
            </Button>
          </Stack>
        )}
      />

      {envReadonly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Voice and chat model tags are configured in <strong>.env</strong> only (view-only here).
          Update <code>OLLAMA_ACTIVE_MODEL</code>, <code>OLLAMA_VOICE_STAGE1_MODEL</code>,{' '}
          <code>VOICE_PROCESS_LLM_PROVIDER</code>, and <code>VOICE_ANALYSIS_QA_LLM_PROVIDER</code>,
          then restart voice workers.
        </Alert>
      )}

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Process worker (after transcription)
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Stage 1 — voice process (LLM backend)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Resolved from <code>VOICE_PROCESS_LLM_PROVIDER</code> and <code>OLLAMA_VOICE_STAGE1_MODEL</code> in .env.
          Stage 1 JSON runs after transcription (structured cleanup, English field, uncertain segments).
        </Typography>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Voice process LLM"
              value={voiceProcessLlm}
              size="small"
              InputProps={{ readOnly: true }}
              helperText="VOICE_PROCESS_LLM_PROVIDER"
              sx={{ minWidth: 220 }}
            />
          </Stack>
          {voiceProcessLlm === 'ollama' && (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Ollama model tag (process worker)"
                value={voiceStage1Model || '—'}
                InputProps={{ readOnly: true }}
                helperText="OLLAMA_VOICE_STAGE1_MODEL"
                sx={{ maxWidth: 480 }}
              />
            </Box>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Analysis QA LLM (voice analysis worker)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Resolved from <code>VOICE_ANALYSIS_QA_LLM_PROVIDER</code> and <code>OLLAMA_ACTIVE_MODEL</code> in .env.
          Used for matrix and boolean QA when the provider is Ollama.
        </Typography>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Analysis QA LLM"
              value={voiceAnalysisQaLlm}
              size="small"
              InputProps={{ readOnly: true }}
              helperText="VOICE_ANALYSIS_QA_LLM_PROVIDER"
              sx={{ minWidth: 220 }}
            />
          </Stack>
          {voiceAnalysisQaLlm === 'ollama' && (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Ollama model tag (analysis QA / chat)"
                value={activeModel || '—'}
                InputProps={{ readOnly: true }}
                helperText="OLLAMA_ACTIVE_MODEL"
                sx={{ maxWidth: 480 }}
              />
            </Box>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            OpenAI organization costs (current UTC month)
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={loadOpenaiCosts}
            disabled={openaiCostsLoading || !canReadModels}
            sx={{ borderColor: PRIMARY, color: PRIMARY }}
          >
            {openaiCostsLoading ? 'Loading…' : 'Refresh costs'}
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Totals come from OpenAI&apos;s Costs API using <code>OPENAI_ADMIN_API_KEY</code> on the server.
          Some accounts return 403/404 until the key and organization are configured correctly.
        </Typography>
        {openaiCosts && (
          <Box>
            {!openaiCosts.configured && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <code>OPENAI_ADMIN_API_KEY</code> is not set on the server.
              </Alert>
            )}
            {openaiCosts.error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {openaiCosts.error}
              </Alert>
            )}
            {openaiCosts.success && openaiCosts.total_usd != null && (
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Period:</strong> {openaiCosts.period_label}
                {' · '}
                <strong>Total (USD):</strong> {Number(openaiCosts.total_usd).toFixed(4)}
                {' · '}
                <strong>Daily buckets:</strong> {openaiCosts.bucket_count}
                {openaiCosts.http_status != null && (
                  <>
                    {' · '}
                    <strong>HTTP:</strong> {openaiCosts.http_status}
                  </>
                )}
              </Typography>
            )}
            {openaiCosts.success === false && openaiCosts.configured && !openaiCosts.error && (
              <Typography variant="body2" color="text.secondary">
                No summary returned. Check server logs.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5, mt: 1 }}>
        Analysis worker
      </Typography>
      {canPullModels && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Pull Ollama tag
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Downloads a model from the registry (same as <code>ollama pull</code>). After pull, point .env at the tag
            and restart workers — this UI does not assign active models.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              label="Model tag"
              placeholder="e.g. qwen3.5:9b"
              value={pullTag}
              onChange={(e) => setPullTag(e.target.value)}
              disabled={pullBusy}
              sx={{ minWidth: 240 }}
            />
            <Button
              variant="contained"
              onClick={handlePullOnly}
              disabled={pullBusy}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#2a3f5f' } }}
            >
              {pullBusy ? <CircularProgress size={22} color="inherit" /> : 'Pull model'}
            </Button>
          </Stack>
        </Paper>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        {(voiceProcessLlm === 'openai' || voiceAnalysisQaLlm === 'ollama') && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            {voiceProcessLlm === 'openai' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Voice process uses <strong>OpenAI</strong> for Stage 1 per .env.
              </Typography>
            )}
            {voiceAnalysisQaLlm === 'ollama' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Analysis QA on <strong>Ollama</strong> uses the <strong>default active</strong> tag. In the table, that
                row shows <strong>Active default</strong> on the same line as the model name and{' '}
                <strong>Analysis QA (Ollama, in use)</strong> in Status.
              </Typography>
            )}
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ minWidth: 360, width: '28%' }}><strong>Model tag</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Overview</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {models.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">
                    No models reported by Ollama. On the server run e.g. <code>ollama pull mistral</code> then refresh.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              models.map((m) => (
                <TableRow key={m.model_name} hover>
                  <TableCell sx={{ minWidth: 360, width: '28%', verticalAlign: 'middle' }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      flexWrap="nowrap"
                      sx={{
                        display: 'inline-flex',
                        maxWidth: '100%',
                        verticalAlign: 'middle',
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        component="span"
                        sx={{
                          minWidth: 0,
                          flexShrink: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.model_name}
                      </Typography>
                      {m.is_current && (
                        <Chip
                          label="Active default"
                          size="small"
                          sx={{ flexShrink: 0, bgcolor: PRIMARY, color: '#fff', fontWeight: 600 }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {m.is_voice_stage1 && voiceProcessLlm === 'ollama' && (
                        <Chip label="Voice Stage 1 (in use)" size="small" color="secondary" variant="outlined" />
                      )}
                      {m.is_voice_stage1 && voiceProcessLlm === 'openai' && (
                        <Chip
                          label="Stage 1 tag stored (OpenAI active)"
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: 'text.disabled', color: 'text.secondary' }}
                        />
                      )}
                      {voiceAnalysisQaLlm === 'ollama' && m.is_current && (
                        <Chip
                          label="Analysis QA (Ollama, in use)"
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#2a3f5f', color: '#2a3f5f', fontWeight: 600 }}
                        />
                      )}
                      {!m.is_current && !m.is_voice_stage1 && (
                        <Chip label="Installed" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'block', maxWidth: 520, lineHeight: 1.45 }}
                    >
                      {m.model_card_description
                        || (m.modelfile_preview ? String(m.modelfile_preview).slice(0, 220) : null)
                        || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                      {canDelete && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setDeleteTarget(m.model_name)}
                        >
                          Delete
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteModel}
        title="Remove Ollama model?"
        severity="warning"
        confirmText="Delete"
        message={
          deleteTarget
            ? `Run ollama rm on the server for "${deleteTarget}". If this tag matches .env (OLLAMA_ACTIVE_MODEL or OLLAMA_VOICE_STAGE1_MODEL), update .env before deleting.`
            : ''
        }
      />

      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Box>
  );
};

export default ModelManagement;
